/**
 * Client-side store for per-user chat preferences.
 *
 * Single in-memory source of truth, hydrated from GET /api/user/chat-preferences
 * on login and synced back with a debounced PATCH on every change. Legacy
 * localStorage keys are migrated to Supabase on first hydrate and then removed.
 *
 * Non-React modules (chatStreamPreferences, thoughtProcessPreferences, services)
 * read synchronously via getChatPreferencesSnapshot(); React components use the
 * useChatPreferences() hook which subscribes to this store.
 */
import {
  DEFAULT_CHAT_PREFERENCES,
  FIELD_DEFS,
  type UserChatPreferences,
} from "@shared/chatPreferences";
import { getCurrentSession } from "@/lib/supabase";

const ENDPOINT = "/api/user/chat-preferences";
const PATCH_DEBOUNCE_MS = 500;

/** Legacy localStorage keys removed after a successful migration to Supabase. */
export const LEGACY_STORAGE_KEYS = [
  "preferred_model",
  "web_search_mode",
  "thinking_mode",
  "auto_select_best",
  "use_personalization",
  "use_source_of_truth",
  "chat_stream_preferences",
  "thought_process_preferences",
] as const;

type Listener = () => void;

let state: UserChatPreferences = { ...DEFAULT_CHAT_PREFERENCES };
let hydratedForUserId: string | null = null;
let hydrating: Promise<void> | null = null;
const listeners = new Set<Listener>();

let pendingPatch: Partial<Record<keyof UserChatPreferences, unknown>> = {};
let patchTimer: ReturnType<typeof setTimeout> | null = null;

function notify() {
  for (const l of listeners) l();
}

export function subscribeChatPreferences(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getChatPreferencesSnapshot(): UserChatPreferences {
  return state;
}

export function isChatPreferencesHydrated(): boolean {
  return hydratedForUserId !== null;
}

function setState(next: UserChatPreferences) {
  state = next;
  notify();
}

function applyValidated(partial: Record<string, unknown>): UserChatPreferences {
  const next = { ...state };
  for (const [key, value] of Object.entries(partial)) {
    const def = FIELD_DEFS[key as keyof UserChatPreferences];
    if (!def) continue;
    if (value === null && DEFAULT_CHAT_PREFERENCES[key as keyof UserChatPreferences] === null) {
      (next as Record<string, unknown>)[key] = null;
      continue;
    }
    const valid = def.validate(value);
    if (valid !== undefined) (next as Record<string, unknown>)[key] = valid;
  }
  return next;
}

async function authHeaders(): Promise<Record<string, string> | null> {
  try {
    const session = await getCurrentSession();
    if (!session?.access_token) return null;
    return {
      Authorization: `Bearer ${session.access_token}`,
      "Content-Type": "application/json",
    };
  } catch {
    return null;
  }
}

async function sendPatch(fields: Record<string, unknown>): Promise<boolean> {
  const headers = await authHeaders();
  if (!headers) return false;
  try {
    const res = await fetch(ENDPOINT, {
      method: "PATCH",
      headers,
      body: JSON.stringify(fields),
    });
    return res.ok;
  } catch {
    return false;
  }
}

function flushPatchSoon() {
  if (patchTimer) clearTimeout(patchTimer);
  patchTimer = setTimeout(async () => {
    patchTimer = null;
    const fields = pendingPatch;
    pendingPatch = {};
    if (Object.keys(fields).length === 0) return;
    const ok = await sendPatch(fields);
    if (!ok) {
      // Re-queue failed fields (newer pending values win) and retry with the next flush.
      pendingPatch = { ...fields, ...pendingPatch };
      if (!patchTimer) {
        patchTimer = setTimeout(() => {
          patchTimer = null;
          flushPatchSoon();
        }, 5000);
      }
    }
  }, PATCH_DEBOUNCE_MS);
}

/**
 * Optimistically update preferences and schedule a debounced server sync.
 * Accepts one field or several at once.
 */
export function updateChatPreferences(partial: Partial<UserChatPreferences>): void {
  setState(applyValidated(partial as Record<string, unknown>));
  Object.assign(pendingPatch, partial);
  flushPatchSoon();
}

// ---------- Legacy localStorage migration ----------

function readLegacyPreferences(): Partial<UserChatPreferences> {
  if (typeof window === "undefined") return {};
  const out: Partial<UserChatPreferences> = {};
  const get = (k: string) => localStorage.getItem(k);

  const model = get("preferred_model");
  if (model) out.preferredModel = model;

  const web = get("web_search_mode");
  if (web === "open" || web === "closed") out.webSearchMode = web;

  const autoBest = get("auto_select_best");
  if (autoBest !== null) out.autoSelectBest = autoBest === "true";

  const personalization = get("use_personalization");
  if (personalization !== null) out.usePersonalization = personalization === "true";

  const sourceOfTruth = get("use_source_of_truth");
  if (sourceOfTruth !== null) out.useSourceOfTruth = sourceOfTruth === "true";

  try {
    const stream = get("chat_stream_preferences");
    if (stream) {
      const parsed = JSON.parse(stream) as Record<string, unknown>;
      if (typeof parsed.timeoutPreset === "string") {
        out.streamTimeoutPreset = parsed.timeoutPreset as UserChatPreferences["streamTimeoutPreset"];
      }
      if (typeof parsed.customTimeoutMinutes === "number") {
        out.streamCustomTimeoutMinutes = parsed.customTimeoutMinutes;
      }
    }
  } catch {
    /* ignore malformed JSON */
  }

  let defaultThinkingMode: boolean | undefined;
  try {
    const thought = get("thought_process_preferences");
    if (thought) {
      const parsed = JSON.parse(thought) as Record<string, unknown>;
      if (typeof parsed.defaultThinkingMode === "boolean") defaultThinkingMode = parsed.defaultThinkingMode;
      if (typeof parsed.defaultRagOn === "boolean") out.useRag = parsed.defaultRagOn;
      if (typeof parsed.ragLimit === "number") out.ragLimit = parsed.ragLimit;
      if (typeof parsed.ragSimilarityThreshold === "number") out.ragSimilarityThreshold = parsed.ragSimilarityThreshold;
      if (typeof parsed.ragHybridMode === "boolean") out.ragHybridMode = parsed.ragHybridMode;
      if (typeof parsed.ragHybridSearch === "boolean") out.ragHybridSearch = parsed.ragHybridSearch;
      if (typeof parsed.ragUseRerank === "boolean") out.ragUseRerank = parsed.ragUseRerank;
      if (typeof parsed.autoEmbedDocuments === "boolean") out.autoEmbedDocuments = parsed.autoEmbedDocuments;
      if (typeof parsed.autoEmbedThreads === "boolean") out.autoEmbedThreads = parsed.autoEmbedThreads;
    }
  } catch {
    /* ignore malformed JSON */
  }

  const thinking = get("thinking_mode");
  if (thinking !== null) out.thinkingMode = thinking === "true";
  else if (defaultThinkingMode !== undefined) out.thinkingMode = defaultThinkingMode;

  return out;
}

function clearLegacyKeys() {
  if (typeof window === "undefined") return;
  for (const key of LEGACY_STORAGE_KEYS) {
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  }
}

// ---------- Hydration ----------

/**
 * Load preferences for the given user. Safe to call repeatedly — a completed
 * hydrate for the same user is a no-op; a user switch re-hydrates.
 */
export function hydrateChatPreferences(userId: string): Promise<void> {
  if (hydratedForUserId === userId) return Promise.resolve();
  if (hydrating) return hydrating;

  hydrating = (async () => {
    const headers = await authHeaders();
    if (!headers) {
      // Not authenticated yet; fall back to legacy values, stay un-hydrated.
      setState(applyValidated(readLegacyPreferences() as Record<string, unknown>));
      return;
    }
    try {
      const res = await fetch(ENDPOINT, { headers });
      if (!res.ok) throw new Error(`GET preferences ${res.status}`);
      const body = (await res.json()) as { preferences: UserChatPreferences; exists: boolean };
      if (body.exists) {
        setState(applyValidated(body.preferences as unknown as Record<string, unknown>));
        hydratedForUserId = userId;
        // Server row is the source of truth now; stale legacy keys just shadow it.
        clearLegacyKeys();
        return;
      }
      // First run for this user: migrate legacy localStorage → Supabase.
      const legacy = readLegacyPreferences();
      const initial = applyValidated(legacy as Record<string, unknown>);
      setState(initial);
      hydratedForUserId = userId;
      const ok = await sendPatch(initial as unknown as Record<string, unknown>);
      if (ok) {
        clearLegacyKeys();
        if (import.meta.env.DEV) {
          console.info("[chatPreferences] migrated localStorage → Supabase", legacy);
        }
      }
      // On failure the row is created by the next successful PATCH; legacy keys stay as fallback.
    } catch {
      // Supabase unreachable: read legacy values, allow a later retry.
      setState(applyValidated(readLegacyPreferences() as Record<string, unknown>));
      hydratedForUserId = null;
    } finally {
      hydrating = null;
    }
  })();
  return hydrating;
}

/** Reset on logout so the next login re-hydrates (and defaults apply meanwhile). */
export function resetChatPreferences(): void {
  if (patchTimer) clearTimeout(patchTimer);
  patchTimer = null;
  pendingPatch = {};
  hydratedForUserId = null;
  hydrating = null;
  setState({ ...DEFAULT_CHAT_PREFERENCES });
}
